import * as Mustache from 'mustache';
import { Templates } from '../../idscan/enums/Templates';
import { TranslationKey } from '../../idscan/enums/Translation';
import { TemplateDictionary } from '../../idscan/interfaces/ContainerConfiguration';
import { PreProcessor, TemplateType } from '../enums/Templates';
import { IFunctionTemplateConfiguration, IPromiseTemplateConfiguration, IRenderableTemplate, IStringTemplateConfiguration, ITemplateConfiguration, IUrlTemplateConfiguration } from '../interfaces/Templates';
import { TemplateFunction } from '../types/Templates';
import { ExceptionManager } from './ExceptionManager';
import LanguageService from './LanguageService';
import RenderingService from './RenderingService';
declare var ENV: string;
/**
 * @internal
 */
export class TemplateService {
	private _templates?: TemplateDictionary;
	private _mustache = new Mustache.Writer();

	public updateTemplates = (templates: Partial<TemplateDictionary>): this => {
		this._templates = {
			...this._templates,
			...templates
		};

		return this;
	}

	public get templates() {
		return { ...this._templates };
	}

	public renderToContainer = <C extends HTMLElement = HTMLElement>(container?: C, templateName: Templates = Templates.None, data: Record<string, any> = {}): Promise<C> => {
		if (ENV === 'DEVELOPMENT') {
			console.log(`Rendering template ${templateName}`);
		}

		return new Promise((resolve, reject) => {
			if (container instanceof HTMLElement) {
				container.innerHTML = '';

				return this.getTemplate(templateName)
					.then((renderableTemplate) => this.processTemplate(container, renderableTemplate, data))
					.then(resolve);
			} else {
				return reject();
			}
		});
	}

	public getElements = <C extends HTMLElement = HTMLElement, E extends {} = {}>(container?: C, elements: Partial<Record<keyof E, string>> = {}): Promise<Partial<Record<keyof E, HTMLElement>>> => {
		return new Promise((resolve, reject) => {
			if (container instanceof HTMLElement) {
				return resolve(Object.keys(elements).reduce((out, element: string) => ({
					...out,
					[element]: container.querySelectorAll((elements as Record<string, string>)[element])[0]
				}), {}));
			} else {
				return reject(new ExceptionManager.TemplateException(TranslationKey.EXCEPTION_TEMPLATE_SOURCE_ELEMENT_MISSING));
			}
		});
	}

	private getTemplate = (templateName: Templates): Promise<IRenderableTemplate> => new Promise((resolve, reject) => {
		const configuration = (this._templates || {})[templateName] as ITemplateConfiguration;
		const { type = TemplateType.Null } = configuration;

		switch (type) {
			// If the provider is a promise, we want to just allow this through
			case TemplateType.Promise:
				{
					const { provider, processor } = configuration as IPromiseTemplateConfiguration;
					return (provider as Promise<string>)
						.then((template) => resolve({ type, processor, template }));
				}
			// If the provider is a function, we want to execute it and return its output
			case TemplateType.Function:
				{
					const { provider, processor } = configuration as IFunctionTemplateConfiguration;
					return resolve({
						processor,
						template: (provider as TemplateFunction)(templateName),
						type,
					});
				}
			// If the provider is a URL, we want to pass it to the `getFile` function to handle
			case TemplateType.Url:
				{
					const { provider, processor } = configuration as IUrlTemplateConfiguration;
					return RenderingService.getFile(provider as string)
						.then((template) => resolve({ type, processor, template }));
				}
			// If the provider is a string, we just want to return that in a resolved promise
			case TemplateType.String:
				{
					const { provider, processor } = configuration as IStringTemplateConfiguration;
					return resolve({ type, processor, template: (provider as string) });
				}
			// Otherwise we want to throw an error
			case TemplateType.Null:
			default:
				return reject(ExceptionManager.process(new ExceptionManager.TemplateException(TranslationKey.EXCEPTION_TEMPLATE_MISSING)));
		}
	})

	private processTemplate = <C extends HTMLElement = HTMLElement>(container: C, { processor, template }: IRenderableTemplate, data: Record<string, any> = {}): Promise<C> => new Promise((resolve) => {
		switch (processor) {
			case PreProcessor.Mustache:
				container.innerHTML = this._mustache.render(template, { ...LanguageService.dictionary, ...data }, {});
				return resolve(container);
			case PreProcessor.None:
			default:
				container.innerHTML = template;
				return resolve(container);
		}
	})
}

export default new TemplateService();
